import { inject, Injectable } from '@angular/core';
import { CASH_REGISTER_STORE } from './cash-register-store.token';
import { CashRegisterSyncLogger } from './cash-register-sync-logger.service';
import {
  CashRegisterClosingDetails,
  CashRegisterEntity,
  CashRegisterOpeningDetails,
  CashRegisterOperationResult,
  CashRegisterStateChange
} from './cash-register-sync.models';
import { requireCashRegisterId } from './cash-register-transitions';

/**
 * Application-level cash register operations — the single entry point every
 * consumer (POS dialogs, pairing flows, the pull sync) uses.
 *
 * Firestore is the source of truth: the store behind `CASH_REGISTER_STORE`
 * performs the operation, enforcing the register's business rules, and this
 * facade records the outcome of every write in Seq. The facade exists so
 * consumers never depend on which store is configured, and so logging happens
 * in one place.
 */
@Injectable({ providedIn: 'root' })
export class CashRegisterService {
  private readonly store = inject(CASH_REGISTER_STORE);
  private readonly syncLogger = inject(CashRegisterSyncLogger);

  //#region Reads

  /** The stored register, or `undefined` when it is absent or unreadable. */
  getByIdAsync(cashRegisterId: string): Promise<CashRegisterEntity | undefined> {
    return this.store.getByIdAsync(cashRegisterId);
  }

  /**
   * The register assigned to a user, active or not — callers apply their own
   * `active` rule, as they did when the Desktop's `ByEmail` view answered this.
   */
  findByActivatedUserAsync(email: string): Promise<CashRegisterEntity | undefined> {
    return this.store.findByActivatedUserAsync(email);
  }

  //#endregion

  //#region Mutations

  /**
   * Open a register. Rejects with a {@link CashRegisterStateError} when a guard
   * refuses. A register the store does not hold yet is created from
   * `cashRegister` (the server's record the POS works from), so a device whose
   * document was never written can still open its register.
   */
  async openAsync<T extends CashRegisterEntity>(
    cashRegister: T,
    opening: CashRegisterOpeningDetails
  ): Promise<CashRegisterOperationResult<T>> {
    requireCashRegisterId(cashRegister);
    return this.log(await this.store.openAsync(cashRegister, opening)) as CashRegisterOperationResult<T>;
  }

  /**
   * Close a register. Rejects with a {@link CashRegisterStateError} when a guard
   * refuses. As with {@link openAsync}, a register the store does not hold yet
   * is created from `cashRegister`, so a shift the store never saw start can
   * still be closed.
   */
  async closeAsync<T extends CashRegisterEntity>(
    cashRegister: T,
    closing: CashRegisterClosingDetails
  ): Promise<CashRegisterOperationResult<T>> {
    requireCashRegisterId(cashRegister);
    return this.log(await this.store.closeAsync(cashRegister, closing)) as CashRegisterOperationResult<T>;
  }

  /** Store the register after a device pairing (creates it when it is not stored yet). */
  async saveAsync<T extends CashRegisterEntity>(cashRegister: T): Promise<CashRegisterOperationResult<T>> {
    return this.log(await this.store.savePairedDeviceAsync(cashRegister));
  }

  /**
   * Copy the server-owned configuration onto the stored register (pull sync).
   * Resolves `undefined` when the register is not stored, so the caller can
   * tell that nothing was written.
   */
  async applyServerConfigurationAsync(
    serverCashRegister: CashRegisterEntity
  ): Promise<CashRegisterOperationResult | undefined> {
    const id = requireCashRegisterId(serverCashRegister);
    return this.log(await this.store.applyServerConfigurationAsync(id, serverCashRegister));
  }

  /** Detach the register from its user and device and deactivate it (pull sync). */
  async unpairAsync<T extends CashRegisterEntity>(
    cashRegister: T
  ): Promise<CashRegisterOperationResult | undefined> {
    const id = requireCashRegisterId(cashRegister);
    return this.log(await this.store.unpairAsync(id));
  }

  //#endregion

  //#region Seq logging

  /**
   * Record the outcome of a write. Every mutation leaves exactly one terminal
   * event: the backend acknowledgement logs a success, a refusal logs a
   * replayable failure, and a skipped stale write logs its own reconciliation
   * record. Logging never affects the result the caller receives.
   */
  private log<R extends CashRegisterOperationResult<any> | undefined>(result: R): R {
    const change: CashRegisterStateChange | undefined = (result as any)?.change;
    if (!result || !change) return result;

    result.firestoreSync.then(outcome => {
      if (outcome.status === 'skipped' && outcome.reason === 'stale') {
        this.syncLogger.logStaleChangeSkipped(change, outcome.existingStamp);
        return;
      }
      if (outcome.status === 'applied') {
        const queuedAt = new Date();
        outcome.acknowledged.then(
          () => this.syncLogger.logSyncSucceeded(change, outcome.created, queuedAt),
          error => this.syncLogger.logSyncFailure(change, error)
        ).catch(() => undefined);
      }
    }).catch(() => undefined);   // a logger that throws must not become an unhandled rejection
    return result;
  }

  //#endregion
}
